import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  CreatePaymentMilestoneInput,
  GqlService,
  UpdatePaymentMilestoneInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchPromises } from '@shared/utils';

import { PaymentMilestoneStore } from './payment-milestone.store';

@Injectable({ providedIn: 'root' })
export class PaymentMilestoneService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private paymentMilestoneStore = inject(PaymentMilestoneStore);

  async add(paymentMilestone: CreatePaymentMilestoneInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createPaymentMilestone$(paymentMilestone)
    );

    if (success && data) {
      this.paymentMilestoneStore.add(data);
    } else {
      this.overlayService.error(errors);
    }

    return {
      data,
      errors,
      success,
    };
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.paymentMilestoneStore.setLoading(true);
        this.paymentMilestoneStore.remove(() => true);

        return this.gqlService.listPaymentMilestones$().pipe(
          map(({ data, errors, success }) => {
            if (success && data) {
              this.paymentMilestoneStore.set(data);
            } else {
              this.overlayService.error(errors);
            }

            this.paymentMilestoneStore.setLoading(false);
          })
        );
      })
    );
  }

  async remove(ids: string[]) {
    const proms: Promise<unknown>[] = [];
    const allErrors: string[][] = [];

    ids.forEach((id) => {
      proms.push(
        firstValueFrom(
          this.gqlService.removePaymentMilestone$(id).pipe(
            tap(({ data, errors, success }) => {
              if (success && data) {
                this.paymentMilestoneStore.remove(id);
              } else {
                allErrors.push(errors);
              }
            })
          )
        )
      );
    });

    await batchPromises(proms, (p) => p);

    if (allErrors.length) {
      this.overlayService.error(...allErrors);
    }

    return !!allErrors.length;
  }

  async update(paymentMilestone: UpdatePaymentMilestoneInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updatePaymentMilestone$(paymentMilestone)
    );

    if (success && data) {
      this.paymentMilestoneStore.update(data.id, data);
    } else {
      this.overlayService.error(errors);
    }

    return {
      data,
      errors,
      success,
    };
  }

  async upsert(upsertData: (CreatePaymentMilestoneInput & { id: null | string })[]) {
    const proms: Promise<{ errors: string[]; success: boolean }>[] = [];
    const allErrors: string[][] = [];

    upsertData.forEach((mil) => {
      const { amount, id, name, organization_id, target_date } = mil;

      if (id) {
        const prom = firstValueFrom(
          this.gqlService
            .updatePaymentMilestone$({ amount, id, name, organization_id, target_date })
            .pipe(
              tap(({ data, errors, success }) => {
                if (success && data) {
                  this.paymentMilestoneStore.update(data.id, data);
                } else {
                  allErrors.push(errors);
                }
              })
            )
        );

        proms.push(prom);
      } else {
        const prom = firstValueFrom(
          this.gqlService
            .createPaymentMilestone$({ amount, name, organization_id, target_date })
            .pipe(
              tap(({ data, errors, success }) => {
                if (success && data) {
                  this.paymentMilestoneStore.add(data);
                } else {
                  allErrors.push(errors);
                }
              })
            )
        );

        proms.push(prom);
      }
    });

    await batchPromises(proms, (p) => p);

    if (allErrors.length) {
      this.overlayService.error(...allErrors);
    }

    return !!allErrors.length;
  }
}
